"use client";

import React, { useState, useEffect } from "react";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import type { Service } from "@/types/services";

// Instead of importing the static JSON, we’ll fetch it dynamically
// from /api/carclasses

// Addresses
const addresses = [
  {
    label: "Sevenoaks",
    value: "West Kingsdown, Sevenoaks TN15 6AR, United Kingdom",
  },
  {
    label: "Bromley",
    value: "50D Homesdale Rd, Bromley BR2 9LD, United Kingdom",
  },
];

// Example "customCarServices" that you had
const customCarServices: Service[] = [
  // NEW CAR
  {
    id: 1001,
    slug: "/new-level-one",
    serviceType: "New Car Services",
    title: "New Car - Level One",
    description: "New car level one package.",
    priceInfo: 0,
    srcts: 0,
  },
  {
    id: 1002,
    slug: "/new-level-two",
    serviceType: "New Car Services",
    title: "New Car - Level Two",
    description: "New car level two package.",
    priceInfo: 0,
    srcts: 0,
  },
  {
    id: 1003,
    slug: "/new-level-three",
    serviceType: "New Car Services",
    title: "New Car - Level Three",
    description: "New car level three package.",
    priceInfo: 0,
    srcts: 0,
  },


];

// Type for the car-classes data we fetch
interface CarClasses {
  [brand: string]: {
    [classKey: string]: string[];
  };
}

/** 
 * Helper: Fetch your WP-based services from:
 *    /api/servicesalldata  (already set up)
 **/
async function fetchServiceData(): Promise<Service[]> {
  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || "http://localhost:3000";
  const apiUrl = `${baseUrl}/api/servicesalldata`;

  const res = await fetch(apiUrl, { cache: "no-store" });
  if (!res.ok) {
    throw new Error(`Failed to fetch service data: ${res.statusText}`);
  }
  return res.json();
}

/** 
 * Helper: Fetch car classes from our Next.js route => which proxies WP data:
 *    GET /api/carclasses
 **/
async function fetchCarData(): Promise<CarClasses> {
  const res = await fetch("/api/carclasses", { cache: "no-store" });
  if (!res.ok) {
    throw new Error("Failed to fetch car-classes data");
  }
  return res.json();
}

const QuoteForm: React.FC = () => {
  const params = useParams();
  const slugParam = params?.slug; // e.g. "new-level-two"
  const router = useRouter();
  const searchParams = useSearchParams(); // for reading ?promocode=XYZ from URL

  // ========== STATE ==========
  const [services, setServices] = useState<Service[]>([]);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    notes: "",
  });
  const [selectedAddress, setSelectedAddress] = useState("");

  // Car Data
  const [carData, setCarData] = useState<CarClasses>({});
  const [selectedMake, setSelectedMake] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [enableManualCar, setEnableManualCar] = useState(false);
  const [manualMake, setManualMake] = useState("");
  const [manualModel, setManualModel] = useState("");

  // PROMO CODE
  const [promoCode, setPromoCode] = useState("");

  // ========== EFFECTS ==========

  // Fetch WordPress-based services + add customCarServices
  useEffect(() => {
    fetchServiceData()
      .then((wpServices) => {
        const combined = [...wpServices, ...customCarServices];
        setServices(combined);

        // If user arrived at /quote/[slug], auto select that service
        if (slugParam) {
          const routeSlug = `/${slugParam}`;
          const matchedService = combined.find((s) => s.slug === routeSlug);
          if (matchedService) {
            setSelectedServices([matchedService.slug]);
          }
        }
      })
      .catch((err) => {
        console.error("Failed to fetch WP services:", err);
        // Fallback to only customCarServices if WP fetch fails
        setServices(customCarServices);
      });
  }, [slugParam]);

  // Fetch Car Classes from /api/carclasses
  useEffect(() => {
    fetchCarData()
      .then((data) => setCarData(data))
      .catch((err) => {
        console.error("Failed to fetch Car Classes:", err);
        // fallback
        setCarData({});
      });
  }, []);

  // Read ?promocode=XYZ from the URL and auto-fill
  useEffect(() => {
    const code = searchParams.get("promocode");
    if (code) {
      setPromoCode(code);
    }
  }, [searchParams]);

  // Group all services by serviceType
  const serviceGroups = services.reduce((acc, service) => {
    const typeKey = service.serviceType?.trim() || "Other";
    if (!acc[typeKey]) {
      acc[typeKey] = [];
    }
    acc[typeKey].push(service);
    return acc;
  }, {} as Record<string, Service[]>);

  const exteriorServices = serviceGroups["Exterior"] || [];
  delete serviceGroups["Exterior"];

  const interiorServices = serviceGroups["Interior"] || [];
  delete serviceGroups["Interior"];

  // ========== HANDLERS ==========
  const handleServiceSelection = (serviceSlug: string) => {
    setSelectedServices((prev) =>
      prev.includes(serviceSlug)
        ? prev.filter((slug) => slug !== serviceSlug)
        : [...prev, serviceSlug]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Convert each selected slug -> title for final submission
    const selectedServiceTitles = selectedServices.map((slug) => {
      const matchedService = services.find((s) => s.slug === slug);
      return matchedService ? matchedService.title : slug;
    });

    // Decide which car data to submit
    const finalCarData = enableManualCar
      ? { make: manualMake, model: manualModel }
      : { make: selectedMake, model: selectedModel };

    const finalData = {
      ...formData,
      location: selectedAddress,
      car: finalCarData,
      selectedServices: selectedServiceTitles,
      // pass the promo code
      promoCode: promoCode,
    };

    try {
      const response = await fetch("/api/quote", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(finalData),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit quote data: ${response.statusText}`);
      }

      const result = await response.json();
      if (result.success) {
        router.push("/quote-sent");
      } else {
        alert(`Error: ${result.message}`);
      }
    } catch (error) {
      console.error("Quote submission error:", error);
      alert("Failed to submit quote. Please try again later.");
    }
  };

  /**
   * Determine the correct link for the "(check the service)" anchor.
   */
  const getServiceLink = (slug: string) => {
    if (slug.startsWith("/new-level-")) {
      return "/new-car-detailing";
    } else if (slug.startsWith("/used-level-")) {
      return "/used-car-detailing";
    }
    return `/services${slug}`;
  };

  // ========== RENDER ==========
  const carDataKeys = Object.keys(carData);
  const isCarDataLoaded = carDataKeys.length > 0;

  return (
    <div
      className="bg-cover bg-center py-8 px-4"
      style={{
        backgroundImage: 'url("/images/about/gallery/image2.jpg")',
      }}
    >
      <div className="bg-white bg-opacity-90 p-6 md:p-10 max-w-5xl mx-auto rounded-lg shadow-md">
        <h2 className="text-2xl font-semibold text-center mb-6">Request a Quote</h2>

        <form onSubmit={handleSubmit}>
          {/* ----- Row for Name, Email, Phone ----- */}
          <div className="grid grid-cols-1 md:grid-cols-3 md:gap-4">
            {/* NAME */}
            <div className="mb-4">
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700"
              >
                Name
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 block w-full py-2 px-3 border border-gray-300 
                           rounded-md shadow-sm focus:ring-indigo-500 
                           focus:border-indigo-500 sm:text-sm"
                required
              />
            </div>

            {/* EMAIL */}
            <div className="mb-4">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1 block w-full py-2 px-3 border border-gray-300 
                           rounded-md shadow-sm focus:ring-indigo-500 
                           focus:border-indigo-500 sm:text-sm"
                required
              />
            </div>

            {/* PHONE */}
            <div className="mb-4">
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700"
              >
                Phone
              </label>
              <input
                type="tel"
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="mt-1 block w-full py-2 px-3 border border-gray-300 
                           rounded-md shadow-sm focus:ring-indigo-500 
                           focus:border-indigo-500 sm:text-sm"
                required
              />
            </div>
          </div>

          {/* ----- NOTES ----- */}
          <div className="mb-4">
            <label
              htmlFor="notes"
              className="block text-sm font-medium text-gray-700"
            >
              Additional Information
            </label>
            <textarea
              id="notes"
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="mt-1 block w-full p-2 border border-gray-300 
                         rounded-md shadow-sm focus:ring-indigo-500 
                         focus:border-indigo-500 sm:text-sm"
            />
          </div>

          {/* ----- PROMO CODE ----- */}
          <div className="mb-4">
            <label
              htmlFor="promoCode"
              className="block text-sm font-medium text-gray-700"
            >
              Promo Code
            </label>
            <input
              type="text"
              id="promoCode"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              className="mt-1 block w-full py-2 px-3 border border-gray-300 
                         rounded-md shadow-sm focus:ring-indigo-500 
                         focus:border-indigo-500 sm:text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              If you have a discount or referral code, enter it here.
            </p>
          </div>

          {/* ----- LOCATION SELECT ----- */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Choose the Branch
            </label>
            <select
              value={selectedAddress}
              onChange={(e) => setSelectedAddress(e.target.value)}
              className="block w-full p-2 border border-gray-300 
                         rounded-md shadow-sm focus:ring-indigo-500 
                         focus:border-indigo-500 sm:text-sm"
              required
            >
              <option value="" disabled>
                -- Select Address --
              </option>
              {addresses.map((addr) => (
                <option key={addr.label} value={addr.value}>
                  {addr.label} - {addr.value}
                </option>
              ))}
            </select>

            <div className="text-xs text-gray-500 mt-2">
              <p>Working hours:</p>
              <ul className="list-disc ml-5">
                <li>Monday-Friday: 9am-7pm</li>
                <li>Saturday: 10am-7pm</li>
                <li>Sunday: By Appointment</li>
              </ul>
            </div>
          </div>

          {/* ----- CAR SELECTION ----- */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Your Car
            </label>

            {/* Automatic Car Selection */}
            {!enableManualCar && (
              <div className="flex flex-col sm:flex-row gap-2 mb-4">
                {/* Select Make */}
                <select
                  className="border p-2 rounded text-black text-sm flex-1"
                  value={selectedMake}
                  onChange={(e) => {
                    setSelectedMake(e.target.value);
                    setSelectedModel("");
                  }}
                  disabled={!isCarDataLoaded}
                >
                  <option value="" disabled>
                    {isCarDataLoaded ? "Select a Brand" : "Loading Brands..."}
                  </option>
                  {Object.keys(carData).map((make) => (
                    <option key={make} value={make}>
                      {make}
                    </option>
                  ))}
                </select>

                {/* Select Model */}
                <select
                  className="border p-2 rounded text-black text-sm flex-1"
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  disabled={!selectedMake || !isCarDataLoaded}
                >
                  <option value="" disabled>
                    {selectedMake ? "Select a Model" : "Select brand first"}
                  </option>
                  {selectedMake &&
                    Object.keys(carData[selectedMake] || {}).map((classKey) =>
                      carData[selectedMake][classKey].map((model) => (
                        <option key={model} value={model}>
                          {model}
                        </option>
                      ))
                    )}
                </select>
              </div>
            )}

            {/* Manual Car Fields */}
            {enableManualCar && (
              <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <input
                  type="text"
                  placeholder="Car Make"
                  value={manualMake}
                  onChange={(e) => setManualMake(e.target.value)}
                  className="border p-2 rounded w-full text-sm"
                />
                <input
                  type="text"
                  placeholder="Car Model"
                  value={manualModel}
                  onChange={(e) => setManualModel(e.target.value)}
                  className="border p-2 rounded w-full text-sm"
                />
              </div>
            )}

            {/* Toggle Checkbox */}
            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="enableManualCar"
                checked={enableManualCar}
                onChange={(e) => {
                  setEnableManualCar(e.target.checked);
                  if (!e.target.checked) {
                    setManualMake("");
                    setManualModel("");
                  }
                }}
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded mr-2"
              />
              <label htmlFor="enableManualCar" className="text-sm">
                I don&apos;t see my car—let me type it manually
              </label>
            </div>
          </div>

          {/* ----- SERVICES SELECTION ----- */}
          <div className="bg-white p-4 md:p-6 rounded-lg mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* LEFT COLUMN: Exterior + Interior */}
              <div>
                {/* Exterior Services */}
                {exteriorServices.length > 0 && (
                  <>
                    <h3 className="text-xl font-bold mb-3">Exterior Services</h3>
                    {exteriorServices.map((service) => {
                      const isChecked = selectedServices.includes(service.slug);
                      const linkHref = getServiceLink(service.slug);

                      return (
                        <div
                          key={service.id}
                          className="mb-3 flex items-center space-x-2"
                        >
                          <input
                            type="checkbox"
                            value={service.slug}
                            checked={isChecked}
                            onChange={() => handleServiceSelection(service.slug)}
                            className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                          />
                          <label className="text-sm font-medium">
                            {service.title}
                            <Link
                              href={linkHref}
                              target="_blank"
                              className="ml-1 text-blue-500 text-xs underline"
                            >
                              (check the service)
                            </Link>
                          </label>
                        </div>
                      );
                    })}
                  </>
                )}

                {/* Interior Services */}
                {interiorServices.length > 0 && (
                  <>
                    <h3 className="text-xl font-bold mb-3">Interior Services</h3>
                    {interiorServices.map((service) => {
                      const isChecked = selectedServices.includes(service.slug);
                      const linkHref = getServiceLink(service.slug);

                      return (
                        <div
                          key={service.id}
                          className="mb-3 flex items-center space-x-2"
                        >
                          <input
                            type="checkbox"
                            value={service.slug}
                            checked={isChecked}
                            onChange={() => handleServiceSelection(service.slug)}
                            className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                          />
                          <label className="text-sm font-medium">
                            {service.title}
                            <Link
                              href={linkHref}
                              target="_blank"
                              className="ml-1 text-blue-500 text-xs underline"
                            >
                              (check the service)
                            </Link>
                          </label>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>

              {/* RIGHT COLUMN: Everything else */}
              <div>
                {Object.keys(serviceGroups).map((type) => (
                  <div key={type} className="mb-5">
                    <h3 className="text-xl font-bold mb-3">{type}</h3>
                    {serviceGroups[type].map((service) => {
                      const isChecked = selectedServices.includes(service.slug);
                      const linkHref = getServiceLink(service.slug);

                      return (
                        <div
                          key={service.id}
                          className="mb-3 flex items-center space-x-2"
                        >
                          <input
                            type="checkbox"
                            value={service.slug}
                            checked={isChecked}
                            onChange={() => handleServiceSelection(service.slug)}
                            className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                          />
                          <label className="text-sm font-medium">
                            {service.title}
                            <Link
                              href={linkHref}
                              target="_blank"
                              className="ml-1 text-blue-500 text-xs underline"
                            >
                              (check the service)
                            </Link>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ----- SUBMIT BUTTON ----- */}
          <button
            type="submit"
            className="mt-6 w-full bg-green-500 text-white py-3 px-4 
                       rounded-md shadow hover:bg-green-600 transition"
          >
            Submit Quote
          </button>
        </form>
      </div>
    </div>
  );
};

export default QuoteForm;
